import axios from 'axios';
import { auth } from './firebase';
import { handleUnauthorizedSession } from '../utils/authSession';

// Backend URL - API Gateway with JWT security definitions
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://tazebijest-gateway-3q76a5yv.uc.gateway.dev';

// Axios instance with centralized configuration
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Refresh lock to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

async function refreshToken(): Promise<string | null> {
    if (isRefreshing && refreshPromise) {
        return refreshPromise;
    }
    isRefreshing = true;
    refreshPromise = (async () => {
        try {
            const currentUser = auth.currentUser;
            if (!currentUser) return null;
            const newToken = await currentUser.getIdToken(true);
            localStorage.setItem('authToken', newToken);
            return newToken;
        } catch {
            return null;
        } finally {
            isRefreshing = false;
            refreshPromise = null;
        }
    })();
    return refreshPromise;
}

// Request interceptor - Auth token ekle
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor - 401'de token yenilemeyi dene, başarısızsa logout
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config as (typeof error.config & {
            _retry?: boolean;
            headers?: Record<string, string>;
        });

        const status = error.response?.status;
        const noResponse = !error.response; // CORS / ağ hatası / gateway JWT reddi
        const isAuthStatus = status === 401 || status === 403;

        // Auth ile ilgisi olmayan normal hatalar (400/404/500 vb.) aynen fırlatılır
        if (!isAuthStatus && !noResponse) {
            return Promise.reject(error);
        }

        // 403 veya response'suz hata + canlı Firebase oturumu varsa:
        // bu bir oturum sorunu değil (RBAC yasaklaması veya geçici ağ hatası) → dokunma
        if ((status === 403 || noResponse) && auth.currentUser) {
            return Promise.reject(error);
        }

        // 401 dışındaki durumlarda (403/response'suz) token yenileme yapılamaz,
        // oturum gerçekten yok demektir → doğrudan login'e yönlendir
        if (status !== 401) {
            await handleUnauthorizedSession();
            return Promise.reject(error);
        }

        if (!originalRequest || originalRequest._retry) {
            await handleUnauthorizedSession();
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        const newToken = await refreshToken();

        if (!newToken) {
            await handleUnauthorizedSession();
            return Promise.reject(error);
        }

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        try {
            return await api(originalRequest);
        } catch (retryError: any) {
            const retryStatus = retryError.response?.status;
            if (retryStatus === 401 || retryStatus === 403 || !retryError.response) {
                await handleUnauthorizedSession();
            }

            return Promise.reject(retryError);
        }
    }
);

export default api;

